import React, { Component } from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { I18n } from 'react-redux-i18n';
import { withRouterV6 } from '../../../helpers/withRouterV6.jsx';
import {
  History,
  ExternalLink,
  Eye,
  ShoppingCart,
  Flag,
  User,
  ShieldCheck,
  UserCheck
} from 'lucide-react';

import get from 'lodash.get';

import * as fundraiserActions from '../store/fundraiserActions';
import { CONTENT_MODERATION_ROUTES } from '../../contentModeration/constants';
import MarkSpamModal from '../../../components/common/MarkSpamModal';
import clear from '../../../components/clear';
import { withPageTitle } from '../../../hocs';
import {
  impersonatePageManager,
  isSystemCredential,
  Session
} from '../../../modules/session';

import BasePageView from '../../../components/common/basePageView';
// Generic PageEntity components
import { PageEntityHeader } from '../../../components/pageEntity/PageEntityHeader';
import { PageEntityDetails } from '../../../components/pageEntity/PageEntityDetails';
import { PageEntityFundraisingDetails } from '../../../components/pageEntity/PageEntityFundraisingDetails';
import { PageEntityAchievements } from '../../../components/pageEntity/PageEntityAchievements';
import { PageEntityManagerInformation } from '../../../components/pageEntity/PageEntityManagerInformation';
import { PageEntityCustomFields } from '../../../components/pageEntity/PageEntityCustomFields';
import { PageEntityAbout } from '../../../components/pageEntity/PageEntityAbout';
import { PageEntityRelationships } from '../../../components/pageEntity/PageEntityRelationships';
import { PageEntityFitnessDetails } from '../../../components/pageEntity/PageEntityFitnessDetails';
import { PageEntityFranchise } from '../../../components/pageEntity/PageEntityFranchise';
import { OriginCard } from '../../../components/common/OriginCard/OriginCard';

import { Record } from '../../../modules/bin/utility';

import config from '../../../constants';
import { pageTypesStrings } from '../../../constants/pageTypes';

interface FundraiserViewPageProps {
  match: any;
  fundraiserActions: any;
  record: any;
  franchiseHistory: any;
  isLoading: boolean;
  isLoadingError: boolean;
  history: any;
  session: Session;
  impersonatePageManager: (accountId: number, urlFull: string) => void;
}

interface FundraiserViewPageState {
  isShowEmptyFields: boolean;
}

class FundraiserViewPageContainer extends Component<FundraiserViewPageProps, FundraiserViewPageState> {
  constructor(props: FundraiserViewPageProps) {
    super(props);    this.state = {
      isShowEmptyFields: false,
    };
  }
  componentDidMount() {
    const fundraiserId = this.props.match.params.id;
    this.props.fundraiserActions.getRecord(fundraiserId);
  }

  componentDidUpdate(prevProps: FundraiserViewPageProps) {
    // Fetch franchise history after fundraiser data loads
    const fundraiser = get(this.props.record, 'data.data');
    const prevFundraiser = get(prevProps.record, 'data.data');
    
    if (fundraiser && !prevFundraiser) {
      const franchiseId = get(fundraiser, 'franchiseId');
      const accountId = get(fundraiser, 'manager.id');
      
      if (franchiseId && accountId) {
        this.props.fundraiserActions.getFranchiseHistory(franchiseId, accountId);
      }
    }
  }

  changeVisibilityEmptyFields = () => {
    this.setState(prevState => ({
      isShowEmptyFields: !prevState.isShowEmptyFields,
    }));
  };

  render() {
    const fundraiser = Record.isRecordReady(this.props.record)
      ? this.props.record.data.data
      : {};
    const organization = Record.isRecordReady(this.props.record)
      ? this.props.record.data.data.campaign.organization
      : {};
    const campaignAllowsFitnessActivities = Record.isRecordReady(this.props.record)
      ? this.props.record.data.data.campaign.features?.activities?.fitnessActivities === true
      : false;
    
    const franchiseHistoryData = Record.isRecordReady(this.props.franchiseHistory)
      ? get(this.props.franchiseHistory, 'data.data.list', [])
      : [];
    const isFranchiseHistoryLoading = Record.isRecordLoading(this.props.franchiseHistory);
    const fundraiserId = get(fundraiser, 'id');
    const urlFull = get(fundraiser, 'urlFull');
    const campaignId = get(fundraiser, 'campaign.id');
    const campaignUrlFull = get(fundraiser, 'campaign.urlFull');
    const saleId = get(fundraiser, 'saleId');
    const managerId = get(fundraiser, 'manager.id');

    // Build primary menu items for the header
    const primaryMenuItems = [
      // Visit page (external link)
      urlFull && {
        key: 'visit-page',
        label: I18n.t('fundraiser.fundraiser-view.actions.view-page'),
        icon: <ExternalLink aria-hidden="true" />,
        href: urlFull,
        target: '_blank'
      },
      // View page donations (internal link)
      fundraiserId && campaignId && {
        key: 'view-donations',
        label: I18n.t('fundraiser.fundraiser-view.actions.view-donations'),
        icon: <Eye aria-hidden="true" />,
        linkTo: `/campaigns/${campaignId}/fundraiser/view/donations/${fundraiserId}`
      },
      // View linked sale (internal link, conditional)
      saleId && {
        key: 'view-linked-sale',
        label: I18n.t('fundraiser.fundraiser-view.actions.view-linked-sale'),
        icon: <ShoppingCart aria-hidden="true" />,
        linkTo: `/sales/view/${saleId}`
      },
      // Visit campaign (external link)
      campaignUrlFull && {
        key: 'visit-campaign',
        label: I18n.t('fundraiser.fundraiser-view.actions.view-campaign'),
        icon: <ExternalLink aria-hidden="true" />,
        href: campaignUrlFull,
        target: '_blank'
      },
      // View account details (internal link, conditional on manager)
      get(fundraiser, 'manager.id') && {
        key: 'view-account',
        label: I18n.t('fundraiser.fundraiser-view.actions.view-account'),
        icon: <User aria-hidden="true" />,
        linkTo: `/accounts/detail/${get(fundraiser, 'manager.id')}`
      }
    ].filter(Boolean);

    const isSystem = isSystemCredential(this.props.session);

    // System-admin-only menu items
    const additionalMenuItems = isSystem && fundraiserId ? [
      {
        key: 'audit-trail',
        label: I18n.t('campaign.performance.header.audit-trail'),
        icon: <History aria-hidden="true" />,
        linkTo: `/system-settings/audit-trail?targetFundraiserId=${fundraiserId}`,
        systemOnly: true
      },
      {
        key: 'moderation-history',
        label: I18n.t('fundraiser.fundraiser-view.actions.moderation-history'),
        icon: <ShieldCheck aria-hidden="true" />,
        linkTo: CONTENT_MODERATION_ROUTES.entityHistory('fundraiser', fundraiserId),
        systemOnly: true
      },
      ...(managerId && urlFull ? [{
        key: 'impersonate-manager',
        label: I18n.t('fundraiser.fundraiser-view.actions.impersonate-manager'),
        icon: <UserCheck aria-hidden="true" />,
        onSelect: () => {
          this.props.impersonatePageManager(managerId, urlFull);
        },
        writeAction: true,
        systemOnly: true
      }] : []),
      ...(!get(fundraiser, 'isSpam') ? [{
        key: 'mark-spam',
        label: I18n.t('fundraiser.fundraiser-view.actions.mark-spam'),
        icon: <Flag aria-hidden="true" />,
        onSelect: () => this.props.fundraiserActions.showMarkSpamConfirmation(),
        writeAction: true,
        systemOnly: true
      }] : [])
    ] : [];

    return (
      <BasePageView
        isLoading={this.props.isLoading}
        isLoadingError={this.props.isLoadingError}
        backLink={config.DEFAULT_FUNDRAISERS_URL}
        backLinkTitle="Back to fundraisers"
      >
        <div className="view-detail">          
          <PageEntityHeader
            entity={fundraiser}
            isShowEmptyFields={this.state.isShowEmptyFields}
            changeVisibilityEmptyFields={this.changeVisibilityEmptyFields}
            history={this.props.history}
            translationBase="fundraiser.fundraiser-view"
            entityTypeOverride={get(fundraiser, 'type', 'fundraiser')}
            primaryMenuItems={primaryMenuItems}
            additionalMenuItems={additionalMenuItems}
          />
          <div className="view-detail-wrapper">
            <div className="view-detail-main-section">
              <PageEntityDetails
                entity={fundraiser}
                isShowEmptyFields={this.state.isShowEmptyFields}
                translationBase="fundraiser.fundraiser-view"
                includeVisibility={true}
              />
              <PageEntityManagerInformation
                entity={fundraiser}
                isShowEmptyFields={this.state.isShowEmptyFields}
                translationBase="fundraiser.fundraiser-view"
              />
              <PageEntityCustomFields
                entity={fundraiser}
                isShowEmptyFields={this.state.isShowEmptyFields}
                translationBase="fundraiser.fundraiser-view"
              />
              <PageEntityAchievements
                entity={fundraiser}
                isShowEmptyFields={this.state.isShowEmptyFields}
                translationBase="fundraiser.fundraiser-view"
              />
              <PageEntityAbout
                entity={fundraiser}
                isShowEmptyFields={this.state.isShowEmptyFields}
                translationBase="fundraiser.fundraiser-view"
              />
              <PageEntityRelationships
                entity={fundraiser}
                translationBase="fundraiser.fundraiser-view"
              />
              <PageEntityFranchise
                franchiseHistory={franchiseHistoryData}
                currentPageId={fundraiserId}
                isShowEmptyFields={this.state.isShowEmptyFields}
                translationBase="fundraiser.fundraiser-view"
                isLoading={isFranchiseHistoryLoading}
                showCurrentPageBadge={true}
                isSystemAdmin={isSystem}
                currentPageType={pageTypesStrings.FUNDRAISER}
              />
            </div>
            <div className="view-detail-side-section">
              <PageEntityFundraisingDetails
                entity={fundraiser}
                organization={organization}
                translationBase="fundraiser.fundraiser-view"
              />
              <OriginCard
                source={get(fundraiser, 'source')}
                isShowEmptyFields={this.state.isShowEmptyFields}
                variant="page"
              />
              {(campaignAllowsFitnessActivities && <PageEntityFitnessDetails
                entity={fundraiser}
                isShowEmptyFields={this.state.isShowEmptyFields}
                translationBase="fundraiser.fundraiser-view"
              />)}
            </div>
          </div>
        </div>
        <MarkSpamModal
          entityId={fundraiserId}
          entity={fundraiser}
          stateSliceKey="fundraiserPage"
          markAsSpam={fundraiserActions.markAsSpam}
          hideConfirmation={fundraiserActions.hideMarkSpamConfirmation}
          getRecord={fundraiserActions.getRecord}
          translationBase="fundraiser.fundraiser-view"
        />
      </BasePageView>
    );
  }
}

const mapState = ({ fundraiserPage, session }: any) => {
  return {
    isLoading: Record.isRecordLoading(fundraiserPage.record),
    session,
    record: fundraiserPage.record,
    franchiseHistory: fundraiserPage.franchiseHistory,
    organizationRecord: fundraiserPage.organizationRecord
  };
};

const mapDispatch = (dispatch: any) => {
  return {
    fundraiserActions: bindActionCreators({
      getRecord: fundraiserActions.getRecord,
      getFranchiseHistory: fundraiserActions.getFranchiseHistory,
      clearRecordMessages: fundraiserActions.clearRecordMessages,
      showMarkSpamConfirmation: fundraiserActions.showMarkSpamConfirmation,
    }, dispatch),
    impersonatePageManager: bindActionCreators(impersonatePageManager, dispatch)
  };
};

const titleResolver = (props: any) => {
  const fundraiserName = get(props, 'record.data.data.name', '-');
  const tab = get(props, 'match.params.view', 'details');
  const capitalizedTab = tab.charAt(0).toUpperCase() + tab.slice(1);
  return I18n.t('page-titles.fundraiser.view', { fundraiserName, tab: capitalizedTab });
};

// @ts-ignore: withRouter not available in v6, but needed for legacy class component
const FundraiserViewPage = withRouterV6(clear(connect(mapState, mapDispatch)(withPageTitle(titleResolver)(FundraiserViewPageContainer))));
export default FundraiserViewPage;
